import { exec } from 'child_process';
import fs from 'fs';
import fse from 'fs-extra';
import { Common } from '../common';

export class PackageUpdater {
    /**
     * Updates package.json file with new package versions
     * @param {string} rootPath Root path of the target repository
     * @param {boolean} internal Flag indicating if upgrade is running for an internal WAC repository
     * @param {boolean} audit Flag indicating if upgrade is running it audit mode
     * @param {string[]} updateSource Array used to emit logs to file
     * @returns {Promise<boolean>} Empty promise indicating function completion
     */
    public async updatePackages(audit: boolean, updateSource: string[], experimental: boolean): Promise<boolean> {
        if (!audit) {
            console.log('Beginning package update.');
        } else {
            console.log('Beginning package update audit.');
        }

        const packageFile = Common.rootPath + '\\package.json';
        const templatePackageFile = Common.cliRootPath + 'templates\\wac-template\\package.json';
        const removeListFile = Common.cliRootPath + 'src\\config\\package-remove-list.json';

        const templatePackages = Common.readFileJSON(templatePackageFile);
        const templatePeerDependencies = templatePackages.peerDependencies;
        const templateDevDependencies = templatePackages.devDependencies;
        const templateScripts = templatePackages.scripts;

        // Use equality operator instead of strict equality operator to check for null or undefined
        if (templatePeerDependencies == null || templateDevDependencies == null) {
            throw new Error(`An unexpected error occurred, template package file is malformed. Please verify file at path ${templatePackageFile} has both "peerDependencies" and "devDependencies" defined.`);
        }

        if (experimental) {
            templatePeerDependencies['@microsoft/windows-admin-center-sdk'] = 'experimental';
            templateDevDependencies['@microsoft/windows-admin-center-sdk'] = 'experimental';
        }

        const removeList = Common.readFileJSON(removeListFile).packages;

        const packages = Common.readFileJSON(packageFile);
        if (packages === null) {
            console.log(`No existing package file exists, creating new package file at path ${packageFile}.`);
            fs.copyFileSync(templatePackageFile, packageFile);
        }

        if (packages.peerDependencies == null) {
            packages.peerDependencies = {};
        }

        if (packages.devDependencies == null) {
            packages.devDependencies = {};
        }

        packages.scripts = templateScripts;

        const peerDependencies = packages.peerDependencies;
        const devDependencies = packages.devDependencies;

        const internal = peerDependencies['@microsoft/windows-admin-center-sdk'] || devDependencies['@microsoft/windows-admin-center-sdk']
            ? false : true;

        if (internal) {
            await this.getMsftSmeVersion([templatePeerDependencies, templateDevDependencies]);
        }

        this.updatePackageObject(templatePeerDependencies, peerDependencies, removeList, internal, updateSource);
        this.updatePackageObject(templateDevDependencies, devDependencies, removeList, internal, updateSource);

        updateSource.push('\n');

        if (!audit) {
            console.log('Finished updating packages, writing to file.');
            fse.writeJSONSync(packageFile, packages, { spaces: 2 });

            console.log('Running npm install, please wait...');
            return new Promise((resolve) => exec('npm install', (error, stdout, stderr) => {
                if (error !== null) {
                    console.log('Error during npm install: ' + error);
                }
                if (stderr !== null) {
                    console.log('Error during npm install: ' + stderr);
                }
                console.log(stdout + '\n');

                resolve(internal);
            }));
        } else {
            console.log('Finished audit of package update.');

            return new Promise(resolve => resolve(internal));
        }
    }

    private updatePackageObject(
        sourceObject: any,
        targetObject: any,
        removeList: string[],
        internal: boolean,
        updateSource: string[]): void {
        this.checkPackageRemoveList(removeList, targetObject, updateSource);

        for (const pack in sourceObject) {
            if ((internal && pack === '@microsoft/windows-admin-center-sdk') ||
                (!internal && (pack.startsWith('@msft-sme') || pack === '@microsoft/windows-admin-center-sdk/tools'))) {
                continue;
            }

            const message = `Package '${pack}' will go to version ${sourceObject[pack]}`;
            console.log(message);
            updateSource.push(message + '\n');

            targetObject[pack] = sourceObject[pack];
        }
    }

    private checkPackageRemoveList(removeList: string[], targetObject: any, updateSource: string[]): void {
        for (const pack of removeList) {
            if (Object.prototype.hasOwnProperty.call(targetObject, pack)) {
                const message = `Package '${pack}' will be removed`;
                console.log(message);
                updateSource.push(message + '\n');

                delete targetObject[pack];
            }
        }
    }

    private getMsftSmeVersion(targets: any[]): Promise<void> {
        return new Promise((resolve) => {
            exec('npm show "@microsoft/windows-admin-center-sdk/tools@2" version --json', (error, stdout, stderr) => {
                if (error || stderr) {
                    console.log('Failed to retrieve latest @msft-sme package version, falling back to pre-set values.');
                    resolve(null);
                }

                const shellPackageFile = Common.cliRootPath + 'src\\config\\shell-package-list.json';
                const shellPackages = Common.readFileJSON(shellPackageFile).packages;

                const versionList = <string[]>JSON.parse(stdout);
                const version = versionList[versionList.length - 1];
                targets.forEach((target) => {
                    for (const pack of shellPackages) {
                        if (target[pack] != null) {
                            target[pack] = version;
                        }
                    }
                });

                resolve();
            });
        });
    }
}
